<template>
  <div class="solicitudes-venta-tab">
    <!-- Header with Filters & Counts -->
    <b-card no-body class="mb-2 shadow-sm">
      <div class="p-2 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div class="d-flex flex-wrap align-items-center">
          <b-button
            v-for="tab in filterTabs"
            :key="tab.value"
            size="sm"
            :variant="selectedEstado === tab.value ? 'primary' : 'outline-secondary'"
            class="mr-1 mb-50 font-weight-bold"
            @click="setFilter(tab.value)"
          >
            {{ tab.label }}
            <b-badge
              :variant="selectedEstado === tab.value ? 'light' : 'secondary'"
              class="ml-50"
            >
              {{ getConteo(tab.value) }}
            </b-badge>
          </b-button>
        </div>

        <div class="d-flex align-items-center mb-50">
          <b-input-group size="sm" style="min-width: 260px;">
            <b-form-input
              v-model="searchTxt"
              placeholder="Buscar por conductor o DNI..."
              @keyup.enter="loadSolicitudes(1)"
            />
            <b-input-group-append>
              <b-button variant="primary" @click="loadSolicitudes(1)">
                <feather-icon icon="SearchIcon" />
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <b-button
            variant="outline-primary"
            size="sm"
            class="ml-1"
            title="Refrescar"
            @click="loadSolicitudes(currentPage)"
          >
            <feather-icon icon="RefreshCwIcon" :class="{ 'spin-anim': loading }" />
          </b-button>
        </div>
      </div>

      <!-- Table -->
      <b-table
        responsive
        striped
        hover
        :items="solicitudes"
        :fields="fields"
        :busy="loading"
        show-empty
        empty-text="No se encontraron solicitudes de venta."
        class="mb-0"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="mr-1" variant="primary" />
            <span>Cargando solicitudes...</span>
          </div>
        </template>

        <!-- ID y Fecha -->
        <template #cell(id)="{ item }">
          <div class="font-weight-bolder text-primary">#{{ item.id }}</div>
          <small class="text-muted">{{ formatDate(item.created_at) }}</small>
        </template>

        <!-- Conductor -->
        <template #cell(empleado)="{ item }">
          <div class="d-flex align-items-center">
            <div class="avatar bg-light-primary mr-1 p-50">
              <feather-icon icon="UserIcon" size="18" />
            </div>
            <div>
              <div class="font-weight-bold text-dark">{{ item.empleado }}</div>
              <small class="text-muted">Doc: {{ item.documento }}</small>
            </div>
          </div>
        </template>

        <!-- Días -->
        <template #cell(cantidad_dias)="{ item }">
          <div class="d-flex align-items-center">
            <b-badge variant="light-info" class="font-medium-1 px-1 py-50 mr-1">
              {{ item.cantidad_dias }} {{ item.cantidad_dias === 1 ? 'día' : 'días' }}
            </b-badge>
            <feather-icon
              v-if="item.fechas && item.fechas.length"
              :id="'fechas-pop-' + item.id"
              icon="CalendarIcon"
              class="cursor-pointer text-muted"
            />
            <b-popover
              v-if="item.fechas && item.fechas.length"
              :target="'fechas-pop-' + item.id"
              triggers="hover"
              placement="top"
            >
              <template #title>Fechas vendidas</template>
              <div class="d-flex flex-wrap" style="max-width: 220px;">
                <b-badge
                  v-for="(f, idx) in item.fechas"
                  :key="idx"
                  variant="secondary"
                  class="mr-25 mb-25"
                >
                  {{ f }}
                </b-badge>
              </div>
            </b-popover>
          </div>
        </template>

        <!-- Monto -->
        <template #cell(monto_total)="{ item }">
          <div class="font-weight-bolder text-success">
            S/ {{ formatMoney(item.monto_total) }}
          </div>
        </template>

        <!-- Solicitado por -->
        <template #cell(solicitado_por_nombre)="{ item }">
          <div>{{ item.solicitado_por_nombre || '-' }}</div>
        </template>

        <!-- Estado -->
        <template #cell(estado)="{ item }">
          <b-badge :variant="estadoBadge(item.estado)" class="text-uppercase px-1 py-50">
            {{ item.estado }}
          </b-badge>
        </template>

        <!-- Detalles / Caja -->
        <template #cell(detalles)="{ item }">
          <div v-if="item.estado === 'aprobada'">
            <div v-if="item.numero_caja" class="text-success small font-weight-bold">
              <feather-icon icon="DollarSignIcon" size="14" /> Caja #{{ item.numero_caja }}
            </div>
            <small class="text-muted d-block">Aprobado por: {{ item.aprobado_por_nombre || '-' }}</small>
            <small v-if="item.comentario" class="text-secondary font-italic d-block">
              Obs: {{ item.comentario }}
            </small>
          </div>
          <div v-else-if="item.estado === 'rechazada'">
            <small class="text-danger font-italic">{{ item.comentario || 'Sin motivo especificado' }}</small>
            <div class="small text-muted">Por: {{ item.aprobado_por_nombre || '-' }}</div>
          </div>
          <div v-else-if="item.estado === 'anulada'">
            <small class="text-secondary font-italic">{{ item.motivo_anulacion || 'Sin motivo' }}</small>
          </div>
          <div v-else>
            <small class="text-warning d-block">Pendiente de revisión</small>
            <small v-if="item.comentario" class="text-secondary font-italic d-block">
              Obs: {{ item.comentario }}
            </small>
          </div>
        </template>

        <!-- Acciones -->
        <template #cell(acciones)="{ item }">
          <div class="d-flex align-items-center" style="gap: 6px;">
            <template v-if="item.estado === 'pendiente'">
              <b-button
                v-if="canApprove"
                size="sm"
                variant="success"
                class="px-1 mr-50"
                @click="confirmAprobar(item)"
              >
                <feather-icon icon="CheckIcon" class="mr-25" /> Aprobar
              </b-button>
              <b-button
                v-if="canApprove"
                size="sm"
                variant="outline-danger"
                class="px-1"
                @click="promptRechazar(item)"
              >
                <feather-icon icon="XIcon" class="mr-25" /> Rechazar
              </b-button>
              <span v-if="!canApprove" class="text-muted small">Sin permisos</span>
            </template>

            <template v-else-if="item.estado === 'aprobada'">
              <b-button
                v-if="canApprove"
                size="sm"
                variant="outline-warning"
                class="px-1"
                @click="promptAnular(item)"
              >
                <feather-icon icon="SlashIcon" class="mr-25" /> Anular
              </b-button>
            </template>
          </div>
        </template>
      </b-table>

      <!-- Footer with Pagination -->
      <div class="p-2 d-flex justify-content-between align-items-center flex-wrap">
        <small class="text-muted">
          Mostrando {{ Math.min((currentPage - 1) * perPage + 1, totalRows) }} a {{ Math.min(currentPage * perPage, totalRows) }} de {{ totalRows }} registros
        </small>
        <b-pagination
          v-if="totalRows > perPage"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          class="mb-0"
          @input="loadSolicitudes"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BBadge,
  BButton,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BPagination,
  BSpinner,
  BPopover,
} from 'bootstrap-vue';
import DiasLibresService from '../service/dias-libres.service';
import { getUserData } from '@/auth/utils';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'SolicitudesVentaTab',
  components: {
    BCard,
    BTable,
    BBadge,
    BButton,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BPagination,
    BSpinner,
    BPopover,
  },
  data() {
    return {
      solicitudes: [],
      conteos: {},
      selectedEstado: '',
      searchTxt: '',
      loading: false,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      fields: [
        { key: 'id', label: 'ID / Fecha', sortable: false },
        { key: 'empleado', label: 'Conductor', sortable: false },
        { key: 'cantidad_dias', label: 'Días', sortable: false },
        { key: 'monto_total', label: 'Monto Total', sortable: false },
        { key: 'solicitado_por_nombre', label: 'Solicitado Por', sortable: false },
        { key: 'estado', label: 'Estado', sortable: false },
        { key: 'detalles', label: 'Detalles / Caja', sortable: false },
        { key: 'acciones', label: 'Acciones', sortable: false },
      ],
    };
  },
  computed: {
    canApprove() {
      const user = getUserData() || {};
      return [1, 2, 3].includes(Number(user.role_id));
    },
    filterTabs() {
      return [
        { label: 'Todas', value: '' },
        { label: 'Pendientes', value: 'pendiente' },
        { label: 'Aprobadas', value: 'aprobada' },
        { label: 'Rechazadas', value: 'rechazada' },
        { label: 'Anuladas', value: 'anulada' },
      ];
    },
  },
  mounted() {
    this.loadSolicitudes();
  },
  methods: {
    getConteo(estado) {
      if (!estado) {
        return (
          (this.conteos.pendiente || 0) +
          (this.conteos.aprobada || 0) +
          (this.conteos.rechazada || 0) +
          (this.conteos.anulada || 0)
        );
      }
      return this.conteos[estado] || 0;
    },
    async loadSolicitudes(page = 1) {
      this.currentPage = page;
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          perpage: this.perPage,
          estado: this.selectedEstado,
          search_txt: this.searchTxt,
        };
        const res = await DiasLibresService.getSolicitudes(params);
        if (res.data) {
          this.solicitudes = res.data.data || [];
          this.totalRows = res.data.total || 0;
          this.conteos = res.data.conteos || {};
        }
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            text: e.response?.data?.message || 'Error al cargar solicitudes de venta.',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    setFilter(estado) {
      this.selectedEstado = estado;
      this.loadSolicitudes(1);
    },
    estadoBadge(estado) {
      switch (estado) {
        case 'pendiente':
          return 'light-warning';
        case 'aprobada':
          return 'light-success';
        case 'rechazada':
          return 'light-danger';
        case 'anulada':
          return 'light-secondary';
        default:
          return 'light-primary';
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatMoney(val) {
      return parseFloat(val || 0).toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async confirmAprobar(item) {
      const textConfirm = item.comentario
        ? `Se aprobará la venta de ${item.cantidad_dias} día(s) por S/ ${this.formatMoney(item.monto_total)} y se creará el movimiento en tesorería.\n\nMotivo/Obs: "${item.comentario}"`
        : `Se aprobará la venta de ${item.cantidad_dias} día(s) por S/ ${this.formatMoney(item.monto_total)} y se creará el movimiento en tesorería.`;

      const confirm = await this.$swal({
        title: '¿Aprobar solicitud de venta?',
        text: textConfirm,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, aprobar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      });

      if (!confirm.isConfirmed) return;

      const user = getUserData() || {};
      const userId = user.id || user.user_id;
      try {
        const res = await DiasLibresService.aprobar({
          id: item.id,
          id_usuario: userId,
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Éxito',
            icon: 'CheckIcon',
            text: res.data?.message || 'Solicitud aprobada correctamente.',
            variant: 'success',
          },
        });
        this.loadSolicitudes(this.currentPage);
        this.$emit('solicitud-procesada');
      } catch (e) {
        const errorMsg =
          e.response?.data?.message ||
          e.response?.data?.error ||
          e.message ||
          'No se pudo aprobar la solicitud.';
        this.$swal({
          title: 'Error al aprobar',
          text: errorMsg,
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        });
      }
    },
    async promptRechazar(item) {
      const { value: comentario, isConfirmed } = await this.$swal({
        title: 'Rechazar solicitud',
        text: `Indica el motivo del rechazo para la solicitud de ${item.empleado}:`,
        input: 'textarea',
        inputPlaceholder: 'Escribe el motivo aquí...',
        showCancelButton: true,
        confirmButtonText: 'Rechazar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      });

      if (!isConfirmed) return;

      const user = getUserData() || {};
      const userId = user.id || user.user_id;
      try {
        const res = await DiasLibresService.rechazar({
          id: item.id,
          id_usuario: userId,
          comentario: comentario || '',
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Solicitud rechazada',
            icon: 'CheckIcon',
            text: res.data?.message || 'La solicitud ha sido rechazada.',
            variant: 'warning',
          },
        });
        this.loadSolicitudes(this.currentPage);
        this.$emit('solicitud-procesada');
      } catch (e) {
        const errorMsg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          'No se pudo rechazar la solicitud.';
        this.$swal({
          title: 'Error al rechazar',
          text: errorMsg,
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        });
      }
    },
    async promptAnular(item) {
      const { value: motivo, isConfirmed } = await this.$swal({
        title: '¿Anular venta aprobada?',
        text: 'Los días vendidos serán liberados y volverán a estar disponibles. Deberás revertir el registro de tesorería manualmente.',
        input: 'textarea',
        inputPlaceholder: 'Motivo de la anulación (obligatorio)...',
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return 'Debes ingresar un motivo para anular la venta.';
          }
        },
        showCancelButton: true,
        confirmButtonText: 'Sí, anular venta',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      });

      if (!isConfirmed) return;

      const user = getUserData() || {};
      const userId = user.id || user.user_id;
      try {
        const res = await DiasLibresService.anular({
          id: item.id,
          id_usuario: userId,
          motivo,
        });
        this.$swal({
          title: 'Venta Anulada',
          html: `<p>${res.data?.message || 'Solicitud anulada.'}</p><div class="alert alert-warning mt-1 text-left p-1"><small>${res.data?.advertencia || ''}</small></div>`,
          icon: 'info',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        });
        this.loadSolicitudes(this.currentPage);
        this.$emit('solicitud-procesada');
      } catch (e) {
        const errorMsg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          'No se pudo anular la solicitud.';
        this.$swal({
          title: 'Error al anular',
          text: errorMsg,
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        });
      }
    },
  },
};
</script>

<style scoped>
.spin-anim {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
